import { and, eq, gt, lt } from '@tanstack/db'
import { useLiveQuery } from '@tanstack/react-db'
import { useMemo } from 'react'
import { DEFAULT_PERIOD_TYPE, DEFAULT_WEEK_START_DAY } from '@/constants/periods'
import { useAuth } from '@/lib/auth'
import { secondsToHours, sumNetWorkSeconds } from '@/lib/dates'
import { getPeriodBounds, getPeriodInfo } from '@/lib/periods'
import { useStore } from '@/lib/pocketbase'
import type { PeriodConfig } from '@/types/periods'
import type { TimeEntriesExpanded } from '@/types/timeTracking'

interface UsePeriodEntriesOptions {
    periodDate?: Date
    periodConfig?: PeriodConfig
}

export function usePeriodEntries({ periodDate, periodConfig }: UsePeriodEntriesOptions = {}) {
    const { user } = useAuth({ throwIfAnon: false })
    const [timeEntriesCollection, jobsCollection, locationsCollection] = useStore(
        'time_entries',
        'jobs',
        'locations'
    )

    const config: PeriodConfig = periodConfig || {
        periodType: DEFAULT_PERIOD_TYPE,
        weekStartDay: DEFAULT_WEEK_START_DAY,
    }

    const { start, end } = getPeriodBounds(periodDate, config)
    const periodInfo = getPeriodInfo(periodDate, config)

    const { isLoading, data: rawEntries = [] } = useLiveQuery(
        q =>
            user
                ? q
                      .from({ entries: timeEntriesCollection })
                      .join(
                          { job: jobsCollection },
                          ({ entries, job }) => eq(entries.job, job.id),
                          'left'
                      )
                      .join(
                          { location: locationsCollection },
                          ({ entries, location }) => eq(entries.location, location.id),
                          'left'
                      )
                      .where(({ entries }) =>
                          and(
                              eq(entries.user, user.id),
                              gt(entries.clock_in, start),
                              lt(entries.clock_in, end)
                          )
                      )
                      .orderBy(({ entries }) => entries.clock_in, 'desc')
                : undefined,
        [user?.id, start, end]
    )

    const entries = useMemo(() => {
        return rawEntries.map(row => {
            const entry = row.entries as TimeEntriesExpanded
            return {
                ...entry,
                expand: {
                    ...entry.expand,
                    job: row.job ?? entry.expand?.job,
                    location: row.location ?? entry.expand?.location,
                },
            }
        })
    }, [rawEntries])

    const completedHours = secondsToHours(
        sumNetWorkSeconds(entries.filter(e => e.status === 'pending' || e.status === 'approved'))
    )

    return {
        entries,
        periodStart: start,
        periodEnd: end,
        periodLabel: periodInfo.label,
        periodType: config.periodType,
        completedHours,
        isLoading,
    }
}
